/* eslint-disable relay/unused-fields */
// terms, term & urlLabel are not used here
// suggestions are being filtered and then are passed to component
// Once isPopularSearchesEnabledByLocale related logic is removed
// searchSuggestions fragment needs to be moved to the component with direct usage
import { useEffect, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { cleanUpRecentSearches, dedupeLocalStorage } from '../utils/sharedSearchBarHelpers.ts';
import { SEARCH_BAR_TRACKING } from '../utils/sharedSearchBarConstants.ts';
import {
    type useSearchSuggestions_viewer$data,
    type useSearchSuggestions_viewer$key,
} from './__generated__/useSearchSuggestions_viewer.graphql.ts';

const viewerFragment = graphql`
    fragment useSearchSuggestions_viewer on Viewer
    @argumentDefinitions(
        query: { type: "String", defaultValue: "" }
        hasQuery: { type: "Boolean", defaultValue: false }
    ) {
        isPopularSearchesEnabledByLocale: isEnabledByLocale(featureName: "POPULAR_SEARCHES")
        searchSuggestions(query: $query, includeTypes: $includeTypes) @include(if: $hasQuery) {
            suggestions {
                type
                terms {
                    term
                    urlLabel
                }
            }
        }
    }
`;

export type Suggestions = NonNullable<
    useSearchSuggestions_viewer$data['searchSuggestions']
>['suggestions'];

type useSearchSuggestionsReturn = {
    suggestions?: Suggestions;
    trackEventLabel: string;
    recentSearches: NonNullable<Suggestions>;
};

function getFilteredSuggestions(
    searchSuggestions: useSearchSuggestions_viewer$data['searchSuggestions'],
    isPopularSearchesEnabledByLocale: useSearchSuggestions_viewer$data['isPopularSearchesEnabledByLocale']
): Suggestions | undefined {
    if (!searchSuggestions?.suggestions) {
        return undefined;
    }
    return searchSuggestions.suggestions.filter(suggestion => {
        if (suggestion?.type !== 'POPULAR') {
            return true;
        }
        return isPopularSearchesEnabledByLocale;
    });
}

export const useSearchSuggestions = ({
    viewer: viewerRef,
}: {
    viewer: useSearchSuggestions_viewer$key;
}): useSearchSuggestionsReturn => {
    const viewer = useFragment(viewerFragment, viewerRef);

    useEffect(() => {
        cleanUpRecentSearches();
    }, []);

    const { isPopularSearchesEnabledByLocale, searchSuggestions } = viewer;

    const { suggestions, trackEventLabel } = useMemo(
        () => ({
            suggestions: getFilteredSuggestions(
                searchSuggestions,
                isPopularSearchesEnabledByLocale
            ),
            trackEventLabel: searchSuggestions
                ? SEARCH_BAR_TRACKING.LABELS.GLOBAL_SEARCH_AUTOCOMPLETE
                : SEARCH_BAR_TRACKING.LABELS.RECENT_SEARCH,
        }),
        [searchSuggestions, isPopularSearchesEnabledByLocale]
    );

    /* eslint-disable-next-line react-hooks/exhaustive-deps -- re-read localStorage when Relay viewer snapshot updates */
    const recentSearches = useMemo(() => dedupeLocalStorage() || [], [viewer]);

    return useMemo(
        () => ({
            suggestions,
            recentSearches,
            trackEventLabel,
        }),
        [suggestions, recentSearches, trackEventLabel]
    );
};
